import { BehaviorSubject } from 'rxjs';
import ResponseHandlerService from './ResponseHandlerService';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import groupEndpoints from 'api/resources/group';
import { actionByRole, setLocalStorageItem } from 'lib/helpers';
import { LOCAL_USER_TYPE } from 'constants/localStorage';
import { IGroupsByCentersParams } from 'hooks/resources/useGroupsByCenters';

interface IPagination {
  current_page: number;
  next_page: number | null;
  per_page: number;
  previous_page: number | null;
  total_entries: number;
  total_pages: number;
}

interface GetTeacherByGroupResponse {
  users: any[];
  pagination: IPagination,
}
export interface IGroup {
  id?: number;
  name?: string;
  babies: any[];
  external_id?: string;
  created_at?: string;
  updated_at?: string;
}

export const defaultGroup = {
  id: undefined,
  name: '',
  babies: [],
};

export interface IGroupsByCentersMetaPagination {
  previous_page?: null;
  current_page: number;
  next_page?: null | string;
  last_page?: null | string;
  total_pages: number;
  total_entries: number;
  per_page: number;
}

const defaultPaginationValues = {
  previous_page: null,
  current_page: 0,
  next_page: null,
  last_page: null,
  total_pages: 0,
  total_entries: 0,
  per_page: 200,
};

class GroupService {
  static instance: GroupService;
  private groupList$: BehaviorSubject <IGroup[]> ;
  private teacherGroupList$: BehaviorSubject <IGroup[]> ;
  private GroupsByCenter$: BehaviorSubject<any>;
  public activeGroup$ = new BehaviorSubject<IGroup>(defaultGroup);
  public GroupsByCentersMeta$: BehaviorSubject<IGroupsByCentersMetaPagination>;
  private babiesAssigned$: BehaviorSubject<any>;
  private respHandler = ResponseHandlerService.getInstance();

  constructor() {
    this.groupList$ = new BehaviorSubject < any > (null);
    this.teacherGroupList$ = new BehaviorSubject < any > (null);
    this.babiesAssigned$ = new BehaviorSubject < any > (false);
    this.GroupsByCenter$ = new BehaviorSubject<any>({});
    this.GroupsByCentersMeta$ = new BehaviorSubject<IGroupsByCentersMetaPagination>(defaultPaginationValues);
  }

  static getInstance(): GroupService {
    if (!GroupService.instance) {
      GroupService.instance = new GroupService();
    }

    return GroupService.instance;
  }

  getGroupsByCentersMetaPagination = () => this.GroupsByCentersMeta$;

  setGroupsByCentersMetaPagination = (
    metaPagination: IGroupsByCentersMetaPagination,
  ) => this.GroupsByCentersMeta$.next(metaPagination);

  setActiveGroup = (group: IGroup) => {
    this.activeGroup$.next(group);
    setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_GROUP_ID, group?.id);
  };

  getBabiesAssigned = () => this.babiesAssigned$;

  getActiveGroup$ = () => this.activeGroup$;

  getGroupsByCenter = () => this.GroupsByCenter$;

  createGroup = async (center: any, classroom: any) => {
    try {
      const { group } = await actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.postGroup(center, classroom),
        admin: () => groupEndpoints.postGroup(center, classroom),
        director: () => groupEndpoints.postGroup(center, classroom),
        teacher: () => groupEndpoints.postGroupAsTeacher(classroom),
      });
      const updatedGroupList = await this.fetchGroups();
      return { createdGroup: group, updatedGroupList };
    } catch (error) {
      return error;
    }
  };

  UpdateGroup = async (groupId?: any, center?: any, classroom?: any) => {
    try {
      const { group } = await actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.putGroupAsShared(groupId, center, classroom),
        admin: () => groupEndpoints.putGroupAsShared(groupId, center, classroom),
        director: () => groupEndpoints.putGroupAsShared(groupId, center, classroom),
        teacher: () => groupEndpoints.putGroup(groupId, classroom),
      });
      const updatedGroupList = await this.fetchGroups();
      return { updatedGroup: group, updatedGroupList };
    } catch (error) {
      return error;
    }
  };

  getTeacherbyGroup = async (centerId?: any, classroomId?: any) => {
    const response: Promise<GetTeacherByGroupResponse | any[]>  = this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.getGroupsTeacherAsAdmin(centerId, classroomId),
        admin: () => groupEndpoints.getGroupsTeacherAsAdmin(centerId, classroomId),
        director: () => groupEndpoints.getGroupsTeacherAsAdmin(centerId, classroomId),
        teacher: () => groupEndpoints.getGroupsTeacherAsTeacher(centerId, classroomId),
      }),
      successCallback: (response: any) => response,
    });

    return new Promise((resolve, reject) => {
      response.then(response => {
        // If the current user is a teacher then the response is paginated, otherwise it returns an array.
        const data = response as GetTeacherByGroupResponse;

        if (data.pagination) {
          return resolve(data.users);
        }

        return resolve(response);
      }).catch(reject);
    });
  };

  assignBabiesToGruop = (
    groupId: number,
    babyList: number[],
    teacher: any = {},
  ) => {
    return groupEndpoints
      .postAssignBabiesToGroup(groupId, {
        teacher_name: teacher.name,
        teacher_email: teacher.email,
        babies: babyList,
      })
      .then(() => {
        this.babiesAssigned$.next(true);
      });
  };

  clearBabiesAssigned = () => this.babiesAssigned$.next(false);

  fetchGroup = (groupId: number, alternativeToken?: string) => groupEndpoints.getGroup(groupId, alternativeToken);

  /**
   * Gets the group and it is used when a resource it's gonna be shared with parents
   *
   * @param groupId
   * @param alternativeToken
   * @param centerId
   */
  findGroup = (groupId: number, alternativeToken?: string, centerId?: number) => {
    return this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.getGroupAsOwnerAdminDirector(centerId as number, groupId),
        admin: () => groupEndpoints.getGroupAsOwnerAdminDirector(centerId as number, groupId),
        director: () => groupEndpoints.getGroupAsOwnerAdminDirector(centerId as number, groupId),
        teacher: () => groupEndpoints.getGroup(groupId, alternativeToken),
      }),
      successCallback: (response: any) => response,
    })
  };

  fetchGroupDetails = (centerId: number, groupId: number) =>
    groupEndpoints.getGroupDetailAsShare(centerId, groupId);

  updateGroup = (groupId: number, data: object) =>
    groupEndpoints.putGroup(groupId, data);

  updateGroupProgress = (groupId: number, data: object) =>
    groupEndpoints.putGroupProgress(groupId, data);

  updateGroupFinishDay = (groupId: number, data: object) =>
    groupEndpoints.putGroupFinishDay(groupId, data);

  // deleteGroup = (groupId: number) => groupEndpoints.deleteGroup(groupId);
  deleteGroupFromCenter = (centerId: number, groupId: number) => {
    return this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.deleteGroupAsOwnerAdmin(centerId, groupId, 'admins'),
        admin: () => groupEndpoints.deleteGroupAsOwnerAdmin(centerId, groupId, 'admins'),
        director: () => groupEndpoints.deleteGroupAsOwnerAdmin(centerId, groupId, 'admins'),
        teacher: () => groupEndpoints.deleteGroupAsTeacher(groupId),
      }),
      successCallback: (res: any) => res,
    });
  }

  fetchGroups = async () => {
    try {
      const groups = await groupEndpoints.getGroups();
      this.groupList$.next(groups);
      return groups;
    } catch (err) {
      return err;
    }
  };

  fetchTeacherGroups = async (centerId: number) => {
    try {
      const groups = await groupEndpoints.getGroupsAsTeacher(centerId);
      const groups_2 = groups?.map((group: any) => ({ ...group, id: Number(group?.external_id) }));
      this.groupList$.next(groups);
      return groups_2;
    } catch (err) {
      return err;
    }
  };

  fetchGroupsByCenter = async (centerId: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.getGroupsByCenterAsOwnerAdmin(centerId),
        admin: () => groupEndpoints.getGroupsByCenterAsOwnerAdmin(centerId),
        director: () => groupEndpoints.getGroupsByCenterAsOwnerAdmin(centerId),
        teacher: () => groupEndpoints.getGroupsByCenterAsTeacher(centerId),
      }),
      successCallback: (res: any) =>
        this.GroupsByCenter$.next({
          ...this.GroupsByCenter$.getValue(),
          [centerId]: res,
        }),
    });

  // getGroups from API CORE - Remove once Back mix the APIs
  getGroupsByCenterCore = (centerId: number) => {
    const promise: Promise<any> = this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.getGroupsByCenterId(centerId),
        admin: () => groupEndpoints.getGroupsByCenterId(centerId),
        director: () => groupEndpoints.getGroupsByCenterId(centerId),
        teacher: () => groupEndpoints.getGroupsByCenterAsTeacher(centerId),
      }),
      successCallback: (res: any) => res,
    });

    return new Promise((resolve, reject) => {
      promise.then((response) => {
        if (response.center) {
          resolve(response);
        } else {
          resolve({
            center: {
              groups: response,
            }
          });
        }
      })
      .catch(reject);
    });
  };

  getGroupList$ = () => this.groupList$;

  setGroupList$ = (groupList: any[]) => this.groupList$.next(groupList);

  getTeacherGroupList$ = () => this.teacherGroupList$;

  fetchGroupsByCenters = (params: IGroupsByCentersParams) => {
    return this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => groupEndpoints.getGroupsByCenters(params),
        admin: () => groupEndpoints.getGroupsByCenters(params),
        director: () => groupEndpoints.getGroupsByCenters(params),
        teacher: () => groupEndpoints.getGroupsByCenters(params),
      }),
      successCallback: (response: any) => response,
    })
  };

}

export default GroupService;
